import type { RouteRecordRaw } from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { type PermissionAction, Role, Scope, type WorkspaceFeatures } from '@metras/frontend-shared/interfaces'

export type ModuleFeatureKey = 'processes' | 'datasheets' | 'directories' | 'documents' | 'workInstructions'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    isPublic?: boolean
    isManagement?: boolean
    icon?: string
    noScroll?: boolean
    permission?: [PermissionAction, Scope]
    excludedRoles?: Role[]
    requiresAiToken?: boolean
    requiresELearning?: boolean
    requiresModule?: ModuleFeatureKey
  }
}

export const routes = []

export const mainNavRoutes: RouteRecordRaw[] = [
  {
    path: '/get-started',
    name: 'GetStarted',
    component: () => import('./pages/GetStarted/index.vue'),
    meta: {
      title: 'Get Started',
      icon: 'feather:play',
      noScroll: true,
      excludedRoles: ['GUEST']
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('./pages/Dashboard/index.vue'),
    meta: { title: 'Dashboard', icon: 'feather:layout' }
  },
  {
    path: '/tasks',
    name: 'Tasks',
    component: () => import('./pages/Tasks/index.vue'),
    meta: {
      title: 'Aufgaben',
      icon: 'feather:check',
      noScroll: true
    },
    children: [
      {
        path: ':id',
        children: [
          {
            path: '',
            name: 'Task',
            meta: { noScroll: true },
            component: () => import('./pages/Tasks/Task.vue')
          },
          {
            path: 'subtask/:subtaskId',
            name: 'Subtask',
            meta: { noScroll: true },
            component: () => import('./pages/Tasks/Task.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/processes',
    meta: {
      title: 'Prozesse',
      icon: 'feather:sliders',
      requiresModule: 'processes'
    },
    children: [
      {
        path: '',
        name: 'Processes',
        component: () => import('./pages/Processes/index.vue')
      },
      {
        path: ':id',
        name: 'Process',
        component: () => import('./pages/Processes/Process.vue')
      }
    ]
  },
  {
    path: '/datasheets',
    meta: {
      title: 'Tabellen',
      icon: 'feather:database',
      requiresModule: 'datasheets'
    },
    children: [
      {
        path: '',
        name: 'Datasheets',
        component: () => import('./pages/Datasheets/index.vue')
      },
      {
        path: ':id',
        name: 'Datasheet',
        component: () => import('./pages/Datasheets/Datasheet.vue')
      }
    ]
  },
  {
    path: '/directories',
    meta: {
      title: 'Verzeichnisse',
      icon: 'feather:list',
      requiresModule: 'directories'
    },
    children: [
      {
        path: '',
        name: 'Directories',
        component: () => import('./pages/Directories/index.vue')
      },
      {
        path: ':id',
        name: 'Directory',
        component: () => import('./pages/Directories/Directory.vue')
      },
      {
        path: ':id/entry/:entryId',
        name: 'Directory Entry',
        component: () => import('./pages/Directories/DirectoryEntry.vue')
      }
    ]
  },
  {
    path: '/work-instructions',
    meta: {
      title: 'Arbeitsanweisungen',
      icon: 'feather:settings',
      requiresModule: 'workInstructions'
    },
    children: [
      {
        path: '',
        name: 'Instructions',
        component: () => import('./pages/Instructions/index.vue')
      },
      {
        path: ':id',
        name: 'Instruction',
        component: () => import('./pages/Instructions/Instruction.vue')
      }
    ]
  },
  {
    path: '/documents',
    meta: {
      title: 'Dokumente',
      icon: 'feather:file',
      requiresModule: 'documents'
    },
    children: [
      {
        path: '',
        name: 'Documents',
        component: () => import('./pages/Documents/index.vue')
      },
      {
        path: ':id',
        name: 'Document',
        component: () => import('./pages/Documents/Document.vue')
      }
    ]
  },
  {
    path: '/ai-assistant',
    name: 'AiAssistant',
    component: () => import('./pages/AiAssistant/index.vue'),
    meta: {
      title: 'KI Assistent',
      icon: 'feather:cpu',
      requiresAiToken: true
    }
  },
  {
    path: '/e-learning',
    name: 'Learndash',
    component: () => import('./pages/Learndash/index.vue'),
    meta: {
      title: 'E-Learning',
      icon: 'feather:award',
      requiresELearning: true
    }
  }
]
export const subNavRoutes: RouteRecordRaw[] = [
  {
    path: '/account',
    name: 'Account',
    component: () => import('./pages/Workspaces/index.vue'),
    meta: {
      title: 'Konto',
      icon: 'feather:user',
      permission: ['read', Scope.Workspace]
    }
  },
  {
    path: '/tutorials',
    name: 'Tutorials',
    component: () => import('./pages/Tutorials.vue'),
    meta: {
      title: 'Tutorials',
      icon: 'feather:book-open',
      excludedRoles: ['GUEST']
    }
  },
  {
    path: '/archive',
    name: 'Archive',
    component: () => import('./pages/Archive.vue'),
    meta: { title: 'Archiv', icon: 'feather:archive' }
  }
]

export const footerNavRoutes: RouteRecordRaw[] = [
  {
    path: '/feedback',
    name: 'Feedback',
    component: () => import('@metras/frontend-shared/pages/Feedback.vue'),
    meta: {
      title: 'Feedback geben',
      icon: '',
      excludedRoles: ['GUEST']
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@metras/frontend-shared/pages/Contact.vue'),
    meta: { title: 'Kontakt', icon: '' }
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: () => import('@metras/frontend-shared/pages/TermsAndConditions.vue'),
    meta: { title: 'AGB', icon: '' }
  },
  {
    path: '/imprint',
    name: 'Imprint',
    component: () => import('@metras/frontend-shared/pages/Imprint.vue'),
    meta: { title: 'Impressum', icon: '' }
  },
  {
    path: '/data-policy',
    name: 'DataPolicy',
    component: () => import('@metras/frontend-shared/pages/DataPolicy.vue'),
    meta: { title: 'Datenschutz', icon: '' }
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: () => import('@metras/frontend-shared/pages/FAQ.vue'),
    meta: {
      title: 'FAQ',
      icon: '',
      excludedRoles: ['GUEST']
    }
  },
  {
    path: '/release-info',
    name: 'ReleaseInfo',
    component: () => import('@metras/frontend-shared/pages/ReleaseInfo.vue'),
    meta: {
      title: 'Release Informationen',
      icon: '',
      excludedRoles: ['GUEST']
    }
  }
]

const scrollBehavior = (to, from, savedPosition) => {
  if (to.hash) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ el: to.hash })
      }, 500)
    })
  }

  if (savedPosition) {
    return savedPosition
  } else if (to.meta.noScroll && from.meta.noScroll) {
    return {}
  } else {
    return { top: 0 }
  }
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior,
  routes: [
    ...mainNavRoutes,
    ...subNavRoutes,
    ...footerNavRoutes,
    ...routes,
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@metras/frontend-shared/pages/Profile.vue'),
      meta: { title: 'User Profil', icon: '' }
    },
    {
      path: '/iso-select',
      name: 'ISO-Select',
      component: () => import('./pages/ISOSelect.vue'),
      meta: { title: 'ISO Select', icon: '' }
    },
    {
      path: '/notifications',
      name: 'Notifications',
      component: () => import('./pages/Notifications.vue'),
      meta: { title: 'Benachrichtigungen', icon: '' }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: 'Dashboard', params: {} },
      meta: { isPublic: true }
    }
  ]
})

router.beforeEach(async (to, from) => {
  if (to.path === from.path || to.meta.isPublic) {
    return
  }

  const auth = useAuthStore()

  if (!auth.accessToken || !auth.isAuthenticated) {
    if (!(to.path === '/') && !(to.path === '/dashboard')) {
      auth.setEntryRedirect(to.fullPath)
    }

    return false
  }

  if (!auth.standard && to.name !== 'ISO-Select') {
    return { name: 'ISO-Select', query: { redirect: to.path } }
  }

  if (to.meta.permission) {
    const [action, scope] = to.meta.permission

    if (!auth.hasPermission(action, scope)) {
      return from
    }
  }

  if (to.meta.requiresModule) {
    try {
      const { data: features } = await axios.get<WorkspaceFeatures>('api-workspace/v1/workspaces/features')
      const key = `${to.meta.requiresModule}Enabled` as keyof WorkspaceFeatures

      if (!features[key]) {
        return from
      }
    } catch {
      return from
    }
  }
})

router.afterEach((to) => {
  const parent = to.matched.find((record) => record.meta.title)
  const parentTitle = parent ? parent.meta.title : null

  document.title = to.meta.title || parentTitle || 'Metras Akkreditierungsplattform'
})

export default router
